// LandPrepPopup.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Typography,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import Divider from "@mui/material/Divider";
import AddIcon from "@mui/icons-material/Add";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const CropProtectionPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [fullImage, setFullImage] = useState(null);
  const [comment, setComment] = useState("");
  const { showAlert } = useAlert();
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [id, setId] = useState("");
  const [editedSprayIndex, setEditedSprayIndex] = useState(null);
  const [chemicalOptions, setChemicalOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errors, setErrors] = useState({
    selectedDate: "",
    formDataSet: [],
  });

  let farmerPlotId = getFarmerPlotId();
  const scrollableContainerStyle = {
    maxHeight: "300px",
    overflowY: "auto",
  };
  const [formDataSet, setFormDataSet] = useState([
    {
      chemicalName: "",
      unit: "",
      dosePerAcre: "",
    },
  ]);

  useEffect(() => {
    const fetchChemicalOptions = async () => {
      try {
        const chemicalResponse = await instance.get(
          "plotengagement/get-chemical-names?stage=CROP_PROTECTION"
        );
        const chemicalOptions = chemicalResponse.data.map(
          (chemical) => chemical.name
        );

        const unitResponse = await instance.get(
          "plotengagement/get-units?stage=CROP_PROTECTION"
        );
        const unitOptions = unitResponse.data.map((unit) => unit.name);
        setChemicalOptions(chemicalOptions);
        setUnitOptions(unitOptions);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchChemicalOptions();
  }, []);

  const rows = [
    {
      label: t("Chemical Name"),
      type: "dropdown",
      options: chemicalOptions,
      key: "chemicalName",
    },
    { label: t("Unit"), type: "dropdown", options: unitOptions, key: "unit" },
    { label: t("Dose per acre"), type: "textfield", key: "dosePerAcre" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-crop-protection?farmer_plot=${farmerPlotId}`
        );
        let data = response.data;
        if (data.length > 0) {
          setExistingData(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);

  const handleEditClick = async (crop_protection_id, index) => {
    try {
      const response = await instance.get(
        `plotengagement/get-crop-protection-by-id?farmer_plot=${farmerPlotId}&crop_protection_id=${crop_protection_id}`
      );
      const specificData = response.data;
      if (specificData && specificData.length > 0) {
        const dataToEdit = specificData[0];
        setId(dataToEdit.id);
        setSelectedDate(new Date(dataToEdit.date_of_activity));
        setComment(dataToEdit.comments);

        const updatedFormDataSet = dataToEdit.chemical_dose.map((dose) => ({
          chemicalName: dose.chemical_name,
          unit: dose.unit,
          dosePerAcre: dose.dose_per_acre,
        }));
        setEditedSprayIndex(index);
        setFormDataSet(updatedFormDataSet);
        setIsEditMode(true);
      }
    } catch (error) {
      console.error("Error fetching specific data:", error);
    }
  };
  function getOrdinalSuffix(number) {
    const suffixes = ["th", "st", "nd", "rd"];
    const lastDigit = number % 10;
    const secondLastDigit = Math.floor((number % 100) / 10);

    if (secondLastDigit === 1 || lastDigit > 3 || lastDigit === 0) {
      return number + suffixes[0];
    } else {
      return number + suffixes[lastDigit];
    }
  }
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setErrors((prevErrors) => ({
      ...prevErrors,
      selectedDate: "", // Clear the error for selectedDate field
    }));
  };
  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };
  const handleChange = (field, value, index) => {
    setFormDataSet((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        [field]: value,
      };
      return newData;
    });
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors.formDataSet];
      newErrors[index] = {
        ...newErrors[index],
        [field]: "", // Clear the error for the changed field
      };
      return {
        ...prevErrors,
        formDataSet: newErrors,
      };
    });
  };
  const handleAddMore = () => {
    const newFormData = {
      chemicalName: "",
      unit: "",
      dosePerAcre: "",
    };
    setFormDataSet((prevData) => [...prevData, newFormData]);
  };
  const handleDeleteForm = (index) => {
    setFormDataSet((prevData) => {
      const newData = [...prevData];
      newData.splice(index, 1);
      return newData;
    });
  };

  const handleCloseFullImage = () => {
    setFullImage(null);
  };
  const validateFields = () => {
    let formValid = true;
    let updatedErrors = { ...errors };

    if (!selectedDate) {
      updatedErrors = {
        ...updatedErrors,
        selectedDate: "Date  is required",
      };
      formValid = false;
    }

    const fieldErrors = formDataSet.map((formData) => {
      let fieldError = {};

      if (!formData.chemicalName) {
        fieldError = {
          ...fieldError,
          chemicalName: "Please select a chemical name",
        };
        formValid = false;
      }

      if (!formData.unit) {
        fieldError = {
          ...fieldError,
          unit: "Please select a unit",
        };
        formValid = false;
      }

      if (!formData.dosePerAcre) {
        fieldError = {
          ...fieldError,
          dosePerAcre: "Dose per acre is required",
        };
        formValid = false;
      }

      return fieldError;
    });

    updatedErrors = {
      ...updatedErrors,
      formDataSet: fieldErrors,
    };

    setErrors(updatedErrors);

    return formValid;
  };

  const handleSubmit = () => {
    const isFormValid = validateFields();
    if (isFormValid) {
      const formData = {
        chemical_dose: formDataSet
          .map((item) => ({
            chemical_name: item["chemicalName"],
            unit: item.unit,
            dose_per_acre: item["dosePerAcre"],
          }))
          .filter(
            (item) => item.chemical_name && item.dose_per_acre && item.unit
          ),
        date_of_activity: selectedDate
          ? selectedDate.toISOString().split("T")[0]
          : null, // Format date as "YYYY-MM-DD"
        comments: comment,
      };
      if (isEditMode) {
        formData.id = id;
      } else {
        formData.farmer_plot = farmerPlotId;
      }
      const apiUrl = isEditMode
        ? "plotengagement/update-crop-protection"
        : "plotengagement/create-crop-protection";
      instance
        .post(apiUrl, formData)
        .then((res) => {
          if (res?.data) {
            setShowSuccessPopup(true);
            setTimeout(() => {
              setShowSuccessPopup(false);
              onClose();
            }, 2000);
          }
        })
        .catch((err) => {
          console.error("error updating/creating crop-protection", err);
          showAlert("error", "Some Error Occured");
        });
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle
                style={{ padding: "0px", fontSize: "14px", fontWeight: 700 }}
              >
                {t("Crop Protection")}
              </DialogTitle>
              <span style={{ fontSize: "11px" }}>
                {t("(Maintain Record of Chemical Spray)")}
              </span>
            </Grid>
            <Grid item xs={2}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
          <div style={{ ...scrollableContainerStyle }}>
            {!isEditMode ? (
              <div style={{ ...scrollableContainerStyle }}>
                {existingData &&
                  existingData.map((data, index) => (
                    <Card key={index} style={{ marginBottom: "10px" }}>
                      <CardContent>
                        <Grid container style={{ marginTop: "1px" }}>
                          <Grid item xs={12}>
                            <InputLabel
                              style={{ fontWeight: 700, fontSize: "14px" }}
                            >
                              {" "}
                              {getOrdinalSuffix(index + 1)}
                              {t("Spray")}
                            </InputLabel>
                          </Grid>
                          <Grid item xs={5}>
                            <InputLabel className={classes.taxtLabel}>
                              {t("Date of Activity")}
                            </InputLabel>
                          </Grid>
                          <Grid item xs={2} style={{ marginTop: "10px" }}>
                            <img
                              src={EditIcon}
                              onClick={() => handleEditClick(data.id, index)}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <Typography style={{ marginTop: "10px" }}>
                              {data.date_of_activity}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Divider style={{ margin: "10px 0" }} />
                        {data.chemical_dose.map((chemical, innerIndex) => (
                          <Grid
                            key={innerIndex}
                            container
                            style={{ marginTop: "10px" }}
                          >
                            <Grid item xs={5}>
                              <InputLabel className={classes.taxtLabel}>
                                {t("Chemical Name")}
                              </InputLabel>
                            </Grid>
                            <Grid item xs={3}>
                              <InputLabel className={classes.taxtLabel}>
                                {t("Unit")}
                              </InputLabel>
                            </Grid>
                            <Grid item xs={4}>
                              <InputLabel className={classes.taxtLabel}>
                                {t("Dose per Acre")}
                              </InputLabel>
                            </Grid>
                            <Grid container>
                              <Grid item xs={5}>
                                <Typography>
                                  {chemical.chemical_name}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography>{chemical.unit}</Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography>
                                  {chemical.dose_per_acre}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        ))}
                        <div style={{ marginTop: "20px" }}>
                          <Typography
                            style={{ fontWeight: 700, fontSize: "13px" }}
                          >
                            {t("Comment")}:
                            <span
                              style={{
                                fontWeight: 500,
                                fontSize: "12px",
                                marginLeft: "10px",
                              }}
                            >
                              {data.comments}
                            </span>
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            ) : null}
          </div>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel
                style={{ fontWeight: 700, fontSize: "14px", marginTop: "20px" }}
              >{`${
                editedSprayIndex !== null
                  ? getOrdinalSuffix(editedSprayIndex + 1) // Use edited spray index if available
                  : existingData
                  ? getOrdinalSuffix(existingData.length + 1) // Use existing data length if no edit
                  : "1st"
              } Spray`}</InputLabel>
            </Grid>
            <Grid item xs={7} style={{ marginTop: "20px" }}>
              <InputLabel style={{ fontSize: "12px" }}>
                {t("Day of Activity")}
              </InputLabel>
            </Grid>

            <Grid item xs={5}>
              <TextField
                placeholder="DD/MM/YYYY"
                fullWidth
                helperText={errors.selectedDate}
                error={!!errors.selectedDate}
                value={selectedDate ? selectedDate.toLocaleDateString() : ""}
                InputProps={{
                  sx: {
                    height: "40px",
                    borderRadius: "5px", // You can adjust this if needed
                    marginTop: "10px",
                    background: "#FFF",
                    border: "1px solid #C7BDBD",
                  },
                  readOnly: true,
                  onClick: () => document.getElementById("date-picker").click(),
                }}
              />
            </Grid>
            <DatePicker
              id="date-picker"
              dateFormat="dd/MM/yyyy"
              selected={selectedDate}
              onChange={handleDateChange}
              className={classes.datepicker}
            />
          </Grid>
          <div>
            <Typography className={classes.taxtTitle}>
              {t("Chemical Dose")}
            </Typography>
          </div>
          <div style={{ ...scrollableContainerStyle }}>
            {formDataSet.map((formData, formIndex) => (
              <div key={formIndex} style={{ padding: "0px" }}>
                {rows.map((row, index) => (
                  <Grid key={index} container style={{ marginTop: "10px" }}>
                    <Grid item xs={6}>
                      <InputLabel className={classes.taxtLabel}>
                        {row.label}
                      </InputLabel>
                    </Grid>
                    <Grid item xs={6}>
                      {row.type === "dropdown" ? (
                        <>
                          <Select
                            value={formData[row.key]}
                            error={
                              errors.formDataSet[formIndex] &&
                              errors.formDataSet[formIndex][row.key]
                                ? true
                                : false
                            }
                            helperText={
                              errors.formDataSet[formIndex] &&
                              errors.formDataSet[formIndex][row.key]
                            }
                            onChange={(event) =>
                              handleChange(
                                row.key,
                                event.target.value,
                                formIndex
                              )
                            }
                            style={{ width: "100%" }}
                            sx={{
                              height: "40px", // Set the desired height
                              border: "1px solid #9C9FA0",
                              borderRadius: "5px",
                            }}
                          >
                            {row.options.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.formDataSet[formIndex] && (
                            <div>
                              {row.key === "chemicalName" &&
                                errors.formDataSet[formIndex].chemicalName && (
                                  <Typography variant="caption" color="error">
                                    {errors.formDataSet[formIndex].chemicalName}
                                  </Typography>
                                )}
                              {row.key === "unit" &&
                                errors.formDataSet[formIndex].unit && (
                                  <Typography variant="caption" color="error">
                                    {errors.formDataSet[formIndex].unit}
                                  </Typography>
                                )}
                            </div>
                          )}
                        </>
                      ) : (
                        <TextField
                          helperText={
                            errors.formDataSet[formIndex] &&
                            errors.formDataSet[formIndex][row.key]
                          }
                          error={
                            errors.formDataSet[formIndex] &&
                            errors.formDataSet[formIndex][row.key]
                              ? true
                              : false
                          }
                          type="number"
                          value={formData[row.key]}
                          onChange={(event) =>
                            handleChange(row.key, event.target.value, formIndex)
                          }
                          style={{ width: "100%" }}
                          size="small"
                          InputProps={{
                            sx: {
                              height: "40px",
                              borderRadius: "5px", // You can adjust this if needed
                              marginTop: "10px",
                              background: "#FFF",
                              border: "1px solid #C7BDBD",
                            },
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                ))}

                {formIndex < formDataSet.length - 1 && (
                  <div>
                    <Divider style={{ margin: "10px 0" }} />
                    <Grid container>
                      <Grid item xs={10}></Grid>
                      <Grid item xs={2}>
                        <IconButton
                          aria-label="delete"
                          onClick={() =>
                            handleDeleteForm(
                              !isEditMode ? formIndex + 1 : formIndex + 1
                            )
                          }
                        >
                          <CloseIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                )}
              </div>
            ))}
          </div>

          <DialogActions style={{ display: "grid" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "10px",
                marginRight: "16px",
                width: "140px",
              }}
            >
              <Button
                onClick={handleAddMore}
                style={{
                  background: "#6FA34E",
                  border: "#538434",
                  borderRadius: "10px",
                  color: "#FFF",
                  textTransform: "none",
                }}
              >
                <AddIcon style={{ marginRight: "5px", fontSize: "15px" }} />{" "}
                {t("Add More")}
              </Button>
            </div>
          </DialogActions>

          {/* Modal for displaying full-size image */}
          <Dialog
            open={Boolean(fullImage)}
            onClose={handleCloseFullImage}
            maxWidth="lg"
          >
            <Grid container justifyContent="flex-end">
              <IconButton
                aria-label="close"
                onClick={handleCloseFullImage}
                style={{ position: "absolute", top: 0, right: 0 }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
            <img
              src={fullImage}
              alt="Full Size"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                margin: "auto",
                display: "block",
              }}
            />
          </Dialog>
          <div style={{ display: "grid", padding: "10px" }}>
            <InputLabel>{t("Comment (Optional)")}</InputLabel>
            <TextField
              multiline
              rows={1}
              value={comment}
              onChange={handleCommentChange}
            />
          </div>
          <div
            style={{
              display: "flex",
              margin: "auto",
              justifyContent: "center",
              width: "140px",
            }}
          >
            <CustomButton onClick={handleSubmit} hideIcon>
              {t("Submit")}
            </CustomButton>
          </div>
        </div>
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default CropProtectionPopup;
